import { useEffect, useState } from 'react';
import { endOfMinute, getTime } from 'date-fns';
import moment from 'moment-timezone';
import { useRecoilState } from 'recoil';

import { dayDateTimeState } from '@store';

import { useGetUserLocation } from './useGetUserLocation';

import { DEBUG } from '../helpers/Platform';
import { TimeUtils } from '../helpers/TimeUtils';

// eslint-disable-next-line no-extend-native
Date.prototype.getCurrent = () =>
  DEBUG && Date.prototype.debugDate ? new Date(Date.prototype.debugDate.getTime()) : new Date();

// Calculates number of seconds left in the minute to set initial refresh value
const calculateRefreshValue = today => {
  const endOfMinuteDate = endOfMinute(today);
  const utcNow = getTime(today) / 1000;
  const utcEndOfMinute = getTime(endOfMinuteDate) / 1000;

  return Math.round(utcEndOfMinute - utcNow) * 1000;
};

/**
 * Standard date and time hook, per https://stackoverflow.com/questions/63219753/how-to-show-time-and-date-in-realtime-in-react-js
 * Using date-fns instead of native JS date functionality for better compatibility with Hermes and older browsers
 * Can expand to add any other formatted time needed - refer to https://date-fns.org/docs/Getting-Started - may need to add 24 hour time format option eventually?
 */
export const useDateTime = () => {
  /**
   * date-fns defaults to en-us locale but can be changed later if needed
   */
  // const locale = `en`; // This can be pulled from user device if needed later
  const [today, setToday] = useState(Date.prototype.getCurrent()); // Save the current date to be able to trigger an update

  const [refreshValue, setRefreshValue] = useState(calculateRefreshValue(today));
  const [dayDateTime, setDayDateTime] = useRecoilState(dayDateTimeState);
  const location = useGetUserLocation();
  const timeZone = location.timeZone ?? TimeUtils.systemTimeZone();

  useEffect(() => {
    const day = moment(today).tz(timeZone).format('dddd');
    const date = moment(today).tz(timeZone).format('dddd, D MMMM');
    const time = moment(today).tz(timeZone).format('h:mm');
    /**
     * Get the UTC timestamp of current time
     * Divide by 1000 to get in seconds, as TWC API responses give UTC timestamps in seconds
     * TWC timestamps are always whole numbers, do we need to round? Does it matter for comparisons and calculations?
     */
    const utcTime = Math.floor(getTime(today) / 1000);

    setDayDateTime({
      day,
      date,
      time,
      utcTime,
      timeZone,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [today, timeZone]);

  useEffect(() => {
    const timer = setInterval(() => {
      const newDate = Date.prototype.getCurrent();
      // Creates an interval which will update the current data every minute
      // This will trigger a rerender every component that uses the useDate hook.

      setToday(newDate);
      setRefreshValue(60000);
    }, refreshValue);

    return () => {
      clearInterval(timer); // Return a function to clear the timer so that it will stop being called on unmount
    };
  }, [refreshValue]);

  const { date, time, utcTime } = dayDateTime;

  return {
    date,
    time,
    utcTime,
  };
};
